"use client";

import { useConnection, useReadContract, useBalance as useWagmiBalance } from "wagmi";

import type { Address } from "viem";
import { ERC20_ABI } from "@/data/contracts/abis";
import type { TokenInfo } from "@/domain/types";
import { formatAmount } from "@/lib/format";

/** ERC20 balance for the connected wallet only — mirrors dotone-swap-v3's
 * src/hooks/useBalance.ts, which deliberately reads through the
 * wallet-connected provider (not the always-on read-only one), so this
 * shows "--" until a wallet is connected.
 *
 * `chainId` reads through that chain's own configured transport
 * (providers/wagmi.ts) regardless of which chain the wallet is currently
 * switched to — needed for the BNB swap panel (features/bnb-swap/SwapPanel.tsx),
 * since a user browsing BNB Chain pools hasn't necessarily switched their
 * wallet to BSC yet (that only happens at swap execution, via
 * useEnsureChain), and without it wagmi silently queries whatever chain the
 * wallet *is* on instead. */
export function useTokenBalance(token: TokenInfo | Address | null | undefined, chainId?: number) {
  const { address } = useConnection();
  const tokenAddress = typeof token === "string" ? token : token?.address;
  const decimals = typeof token === "string" ? 18 : (token?.decimals ?? 18);

  const { data, isLoading, refetch } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    chainId,
    query: { enabled: Boolean(tokenAddress && address) },
  });

  const raw = data ?? null;
  return {
    raw,
    display: raw !== null ? formatAmount(raw, decimals) : "",
    loading: isLoading,
    refetch,
  };
}

export function useNativeBalance(chainId?: number) {
  const { address } = useConnection();
  const { data, isLoading, refetch } = useWagmiBalance({ address, chainId, query: { enabled: Boolean(address) } });
  return {
    raw: data?.value ?? null,
    display: data ? formatAmount(data.value, data.decimals) : "",
    loading: isLoading,
    refetch,
  };
}
